import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, of } from 'rxjs';
import { ModalComponent } from '../../../../shared/components/ui/modal/modal.component';
import {
  MeetingRescheduleLogRequest,
  MeetingResponse,
  MeetingStatusResponse,
} from '../../models/meeting.model';
import { MeetingApiService } from '../../services/meeting-api.service';
import { MeetingStatusApiService } from '../../services/meeting-status-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { findRescheduleStatusId, toMeetingRequest } from '../meeting-action.util';

/**
 * Modal de REPROGRAMACIÓN de reunión — reutilizable desde lista, calendario y kanban.
 *
 * Compone las operaciones existentes:
 *  - PUT  /api/meetings/{id}                              → mueve la fecha/hora (y estado si aplica)
 *  - POST /api/meetings/{id}/reschedule-logs (reschedule) → deja el registro en el historial
 *
 * @Input targetStatusId — estado destino explícito (columna de kanban "Reprogramado").
 *   Si no se pasa, se busca en el catálogo un estado cuyo nombre represente reprogramación.
 */
@Component({
  selector: 'app-meeting-reschedule-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  templateUrl: './meeting-reschedule-dialog.component.html',
})
export class MeetingRescheduleDialogComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() meeting: MeetingResponse | null = null;
  @Input() targetStatusId?: number;

  /** Modal cerrado sin reprogramar (X / backdrop / botón "Volver"). */
  @Output() cancelled = new EventEmitter<void>();
  /** Reprogramación aplicada correctamente. Emite la reunión actualizada. */
  @Output() confirmed = new EventEmitter<MeetingResponse>();

  private statuses: MeetingStatusResponse[] = [];
  saving = false;

  // Formulario
  nuevoInicio = ''; // datetime-local "YYYY-MM-DDTHH:mm"
  motivo = '';
  /** INC 79: tope inferior del selector — nunca antes de ahora. */
  minInicio = '';
  /** INC 79: la reunión que se reprograma ya había vencido (se avisa en el diálogo). */
  originalVencida = false;

  constructor(
    private meetingApi: MeetingApiService,
    private statusApi: MeetingStatusApiService,
    private authService: AuthService,
    private toast: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen && this.meeting) {
      this.resetForm();
      this.loadCatalogs();
    }
  }

  /**
   * INC 79 — nunca se precarga una fecha pasada.
   *
   * Antes se precargaba SIEMPRE `meeting.datetimeStart`. Sobre una reunión vencida eso
   * dejaba el formulario con una fecha del pasado y, al confirmar sin tocarla, el PUT del
   * backend no la veía como "movida" (su guarda es `if (startMoved)`) → la reunión quedaba
   * "Reprogramada" a una fecha ya vencida. Ahora, si la original venció, se propone la
   * próxima hora en punto o y media.
   */
  private resetForm(): void {
    const ahora = new Date();
    this.minInicio = this.toLocalInput(ahora.toISOString());

    const original = this.meeting?.datetimeStart ? new Date(this.meeting.datetimeStart) : null;
    this.originalVencida =
      !!original && !isNaN(original.getTime()) && original.getTime() <= ahora.getTime();

    this.nuevoInicio = this.originalVencida
      ? this.toLocalInput(this.nextSlot(ahora).toISOString())
      : this.toLocalInput(this.meeting?.datetimeStart);
    this.motivo = '';
    this.saving = false;
  }

  /** Próxima marca de :00 o :30 estrictamente futura, como propuesta de reprogramación. */
  private nextSlot(from: Date): Date {
    const d = new Date(from.getTime());
    d.setSeconds(0, 0);
    d.setMinutes(d.getMinutes() < 30 ? 30 : 60);
    return d;
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private loadCatalogs(): void {
    this.statusApi
      .listActive(this.businessId)
      .pipe(catchError(() => of([] as MeetingStatusResponse[])))
      .subscribe((data) => (this.statuses = data));
  }

  /** ISO UTC → "YYYY-MM-DDTHH:mm" en hora local para el input datetime-local. */
  private toLocalInput(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  onClose(): void {
    if (this.saving) return;
    this.cancelled.emit();
  }

  confirmar(): void {
    if (!this.meeting?.id || this.saving) return;

    if (!this.nuevoInicio) {
      this.toast.error({ title: 'Fecha requerida', message: 'Seleccione la nueva fecha y hora.' });
      return;
    }

    const meeting = this.meeting;
    const meetingId = meeting.id;
    const businessId = this.businessId;

    const newStart = new Date(this.nuevoInicio);
    if (isNaN(newStart.getTime())) {
      this.toast.error({ title: 'Fecha inválida', message: 'La nueva fecha y hora no es válida.' });
      return;
    }

    // INC 79: el `min` del input no impide teclear una fecha pasada, así que se valida aquí.
    if (newStart.getTime() <= Date.now()) {
      this.toast.error({
        title: 'Fecha pasada',
        message:
          'La nueva fecha y hora de la reunión debe ser posterior a la fecha y hora actuales.',
      });
      return;
    }

    const origStart = new Date(meeting.datetimeStart);
    const origEnd = meeting.datetimeEnd ? new Date(meeting.datetimeEnd) : null;
    const durationMs =
      origEnd && !isNaN(origEnd.getTime()) && !isNaN(origStart.getTime())
        ? origEnd.getTime() - origStart.getTime()
        : 30 * 60000;
    const newEnd = new Date(newStart.getTime() + durationMs);

    const newStartIso = newStart.toISOString();
    const newEndIso = newEnd.toISOString();

    const rescheduleStatusId = this.targetStatusId ?? findRescheduleStatusId(this.statuses);

    const body = toMeetingRequest(meeting);
    body.datetimeStart = newStartIso;
    body.datetimeEnd = newEndIso;
    if (rescheduleStatusId != null) body.idMeetingStatus = rescheduleStatusId;

    const reasonText = this.motivo.trim() || undefined;

    this.saving = true;
    this.meetingApi.updateMeeting(meetingId, businessId, body).subscribe({
      next: (updated) => {
        const currentUserId = this.authService.getCurrentUser()?.id;
        const logReq: MeetingRescheduleLogRequest = {
          idMeeting: meetingId,
          action: 'reschedule',
          originalDatetimeStart: meeting.datetimeStart,
          originalDatetimeEnd: meeting.datetimeEnd ?? meeting.datetimeStart,
          newDatetimeStart: newStartIso,
          newDatetimeEnd: newEndIso,
          reasonText,
          initiatedBy: 'host',
          userInitiatedId: currentUserId != null ? +currentUserId : undefined,
          rescheduleNumber: (meeting.rescheduleCount ?? 0) + 1,
        };
        this.meetingApi
          .createRescheduleLog(meetingId, logReq)
          .pipe(catchError(() => of(null)))
          .subscribe(() => {
            this.saving = false;
            this.toast.success({
              title: 'Reunión reprogramada',
              message: `Se reprogramó la reunión "${updated.title}".`,
            });
            // El backend omite el correo al cliente cuando la reunión quedó sin
            // enlace de videollamada y sin lugar presencial: hay que avisarlo.
            if (updated.confirmationEmailSkipped === true) {
              this.toast.warning({
                title: 'El correo de confirmación NO se envió al cliente',
                message:
                  updated.confirmationEmailSkippedReason ||
                  'La reunión no tiene enlace de videollamada ni lugar presencial. Edítala para completar la modalidad y vuelve a guardar.',
                duration: 0,
              });
            }
            this.confirmed.emit(updated);
          });
      },
      error: (err) => {
        this.saving = false;
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo reprogramar la reunión.',
        });
      },
    });
  }
}
